import { useCallback, useEffect, useState } from "react";
import { Car, Drop, Wrench, Camera, CalendarCheck, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const pickImage = (caption) => new Promise((resolve) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.capture = "environment";
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return resolve("");
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    try {
      const { data } = await api.post("/images", form);
      resolve(data.id);
    } catch (e) { oops(e); resolve(""); }
  };
  input.click();
});

export default function CarCare() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState({
    name: "", kind: "car wash", where: "", radius_miles: 10, licence_authority: "",
    licence_number: "", insurance_number: "", phone: "", note: "",
    services: ["wash_at_work", "vacuum_valet"],
  });
  const [prices, setPrices] = useState({ wash_at_work: 20, vacuum_valet: 30 });
  const [round, setRound] = useState({
    business_id: "", day: "Tuesday", where: "", from_time: "09:00", to_time: "13:00", slots: 6,
  });
  const [bk, setBk] = useState({
    business_id: "", service: "wash_at_work", day: "", from_time: "09:00", to_time: "17:00",
    round_id: "", reg: "", make: "", model: "", size: "medium", colour: "",
    parked_at: "", keys_note: "", address: "", pay: "online", repeat_weeks: 0, note: "",
  });

  const load = useCallback(() => {
    api.get("/car").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const register = () => act(() => api.post("/car/businesses", {
    ...biz,
    radius_miles: Number(biz.radius_miles) || 0,
    prices: Object.fromEntries(biz.services.map((s) => [s, {
      small: Number(prices[s] || 0) * 0.9, medium: Number(prices[s] || 0),
      large: Number(prices[s] || 0) * 1.15, suv: Number(prices[s] || 0) * 1.25,
      van: Number(prices[s] || 0) * 1.4,
    }])),
  }), "Business sent for approval");

  const publishRound = () => act(() => api.post("/car/rounds", {
    ...round, business_id: round.business_id || data.my_businesses[0]?.id, slots: Number(round.slots) || 1,
  }), "Round published");

  const bookIt = () => act(() => api.post("/car/bookings", {
    ...bk, repeat_weeks: Number(bk.repeat_weeks) || 0,
  }), "Slot booked");

  const payFor = async (b) => {
    try {
      const { data: out } = await api.post("/payments/car/checkout", {
        booking_id: b.id, origin_url: window.location.origin,
      });
      window.location.href = out.url;
    } catch (e) { oops(e); }
  };

  const finish = async (b) => {
    const before = await pickImage(`${b.reg} before`);
    if (!before) return;
    toast.info("Now the after photograph");
    const after = await pickImage(`${b.reg} after`);
    if (!after) return;
    act(() => api.post(`/car/bookings/${b.id}/done`, {
      before_image_id: before, after_image_id: after, next_due_months: 0,
    }), "Job done, photographs on the booking");
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Opening the car care desk…</p>;

  const toggle = (s) => setBiz({
    ...biz,
    services: biz.services.includes(s) ? biz.services.filter((x) => x !== s) : [...biz.services, s],
  });

  return (
    <div className="space-y-6" data-testid="car-care-page">
      <div>
        <p className="overline text-[#B3241B]">Car care at work</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Car size={30} weight="duotone" className="text-[#002FA7]" /> Washed while you work
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="car-care-explainer">
          Book the car in while you are on shift — washed where it sits in the car park, keys
          collected and brought back clean, a mobile van with a vacuum catching you on the way
          home, or a routine check-up at the garage. Every finished job carries a before and an
          after photograph, and a check-up books its own reminder for next time.
        </p>
      </div>

      {/* ---- Book a slot ---- */}
      <section className="border border-border bg-white p-4 sm:p-5" data-testid="car-book-panel">
        <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">Book a slot</h2>
        {data.businesses.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600" data-testid="car-no-businesses">
            No wash or garage is approved round here yet. If you run one, put it up below.
          </p>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <select data-testid="car-book-business" aria-label="Business" value={bk.business_id}
              onChange={(e) => setBk({ ...bk, business_id: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value="">Who is doing it?</option>
              {data.businesses.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.kind}</option>
              ))}
            </select>
            <select data-testid="car-book-service" aria-label="Service" value={bk.service}
              onChange={(e) => setBk({ ...bk, service: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              {data.services.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <select data-testid="car-book-round" aria-label="Round" value={bk.round_id}
              onChange={(e) => setBk({ ...bk, round_id: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value="">Any time (no round)</option>
              {data.rounds.filter((r) => !bk.business_id || r.business_id === bk.business_id).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.day} · {r.where} · {r.from_time}–{r.to_time}
                </option>
              ))}
            </select>
            <Input data-testid="car-book-day" type="date" value={bk.day}
              onChange={(e) => setBk({ ...bk, day: e.target.value })} />
            <Input data-testid="car-book-from" type="time" value={bk.from_time}
              onChange={(e) => setBk({ ...bk, from_time: e.target.value })} />
            <Input data-testid="car-book-to" type="time" value={bk.to_time}
              onChange={(e) => setBk({ ...bk, to_time: e.target.value })} />
            <Input data-testid="car-book-reg" placeholder="Registration" value={bk.reg}
              onChange={(e) => setBk({ ...bk, reg: e.target.value })} />
            <Input data-testid="car-book-make" placeholder="Make and model" value={bk.make}
              onChange={(e) => setBk({ ...bk, make: e.target.value })} />
            <select data-testid="car-book-size" aria-label="Car size" value={bk.size}
              onChange={(e) => setBk({ ...bk, size: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              {data.sizes.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
            <Input data-testid="car-book-parked" placeholder="Where is it parked?" value={bk.parked_at}
              onChange={(e) => setBk({ ...bk, parked_at: e.target.value })} />
            <Input data-testid="car-book-keys" placeholder="Keys — where and who with" value={bk.keys_note}
              onChange={(e) => setBk({ ...bk, keys_note: e.target.value })} />
            <select data-testid="car-book-pay" aria-label="How to pay" value={bk.pay}
              onChange={(e) => setBk({ ...bk, pay: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value="online">Pay online</option>
              <option value="on_handover">Pay the washer on handover</option>
            </select>
            <select data-testid="car-book-repeat" aria-label="Repeat" value={bk.repeat_weeks}
              onChange={(e) => setBk({ ...bk, repeat_weeks: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              <option value={0}>One off</option>
              <option value={1}>Every week</option>
              <option value={2}>Every 2 weeks</option>
              <option value={4}>Every month</option>
              <option value={26}>Every 6 months (check-up)</option>
            </select>
            <div className="sm:col-span-2">
              <Button data-testid="car-book-submit" disabled={busy} onClick={bookIt}>
                <CalendarCheck size={16} className="mr-1.5" /> Book it
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* ---- My bookings ---- */}
      <section data-testid="car-my-bookings">
        <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">My cars</h2>
        {data.my_bookings.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600" data-testid="car-my-bookings-empty">
            Nothing booked in yet.
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {data.my_bookings.map((b, i) => (
              <div key={b.id} className="border border-border bg-white p-4" data-testid={`car-booking-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#0F172A]">
                      {b.reg} · {b.service_label}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {b.business_name} · {b.day} {b.from_time}–{b.to_time} · {b.where_done}
                      {b.parked_at ? ` · ${b.parked_at}` : ""}
                    </p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {b.status}
                      {b.total > 0 ? ` · ${b.symbol}${Number(b.total).toFixed(2)}` : ""}
                      {b.payment_status === "paid" ? " · paid" : ""}
                      {b.next_due ? ` · next due ${b.next_due}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {b.pay === "online" && b.payment_status === "unpaid" && b.status !== "cancelled" && (
                      <Button data-testid={`car-pay-${i}`} size="sm" onClick={() => payFor(b)}>Pay now</Button>
                    )}
                    {b.status !== "done" && b.status !== "cancelled" && (
                      <Button data-testid={`car-cancel-${i}`} size="sm" variant="outline" disabled={busy}
                        onClick={() => act(() => api.post(`/car/bookings/${b.id}/cancel`), "Cancelled")}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                {(b.before_image_url || b.after_image_url) && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {b.before_image_url && (
                      <img src={`${BACKEND}${b.before_image_url}`} alt="Before" loading="lazy"
                        className="h-32 w-full border border-border object-cover"
                        data-testid={`car-before-${i}`} />
                    )}
                    {b.after_image_url && (
                      <img src={`${BACKEND}${b.after_image_url}`} alt="After" loading="lazy"
                        className="h-32 w-full border border-border object-cover"
                        data-testid={`car-after-${i}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Check-ups due ---- */}
      {data.due.length > 0 && (
        <section className="border-l-2 border-[#B3241B] bg-[#FFECEB] px-4 py-3" data-testid="car-due">
          <p className="overline text-[#B3241B]">Check-ups coming up</p>
          <ul className="mt-1.5 space-y-1">
            {data.due.map((d, i) => (
              <li key={d.id} className="text-sm text-[#0F172A]" data-testid={`car-due-${i}`}>
                <span className="font-semibold">{d.reg}</span> — {d.service_label} due {d.next_due}
                {d.mileage ? ` · last done at ${d.mileage}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---- Owner: work coming in ---- */}
      {data.my_businesses.length > 0 && (
        <section data-testid="car-work">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Work booked in with me
          </h2>
          {data.work.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="car-work-empty">
              Nothing booked in yet. Publish a round so staff can pick a slot.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {data.work.map((b, i) => (
                <div key={b.id} className="border border-border bg-white p-4" data-testid={`car-work-${i}`}>
                  <p className="font-semibold text-[#0F172A]">{b.reg} · {b.service_label}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {b.name} · {b.day} {b.from_time}–{b.to_time}
                    {b.keys_note ? ` · keys: ${b.keys_note}` : ""}
                  </p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {b.status} · {b.symbol}{Number(b.total || 0).toFixed(2)} · {b.pay === "online" ? "paying online" : "paying on handover"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {b.status === "booked" && (
                      <Button data-testid={`car-confirm-${i}`} size="sm" disabled={busy}
                        onClick={() => act(() => api.post(`/car/bookings/${b.id}/confirm`), "Confirmed")}>
                        <CheckCircle size={15} className="mr-1.5" /> Confirm
                      </Button>
                    )}
                    {b.status !== "done" && b.status !== "cancelled" && (
                      <Button data-testid={`car-done-${i}`} size="sm" variant="outline" disabled={busy}
                        onClick={() => finish(b)}>
                        <Camera size={15} className="mr-1.5" /> Before &amp; after, done
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ---- Owner: publish a round ---- */}
      {data.my_businesses.length > 0 && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="car-round-panel">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Publish a round
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            "Tuesday, Riverside yard, 9am to 1pm" — staff then just pick a slot off it.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <select data-testid="car-round-business" aria-label="My business" value={round.business_id}
              onChange={(e) => setRound({ ...round, business_id: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              {data.my_businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select data-testid="car-round-day" aria-label="Day" value={round.day}
              onChange={(e) => setRound({ ...round, day: e.target.value })}
              className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
              {data.days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <Input data-testid="car-round-where" placeholder="Where — yard, car park, site" value={round.where}
              onChange={(e) => setRound({ ...round, where: e.target.value })} />
            <Input data-testid="car-round-slots" type="number" min="1" placeholder="Slots" value={round.slots}
              onChange={(e) => setRound({ ...round, slots: e.target.value })} />
            <Input data-testid="car-round-from" type="time" value={round.from_time}
              onChange={(e) => setRound({ ...round, from_time: e.target.value })} />
            <Input data-testid="car-round-to" type="time" value={round.to_time}
              onChange={(e) => setRound({ ...round, to_time: e.target.value })} />
            <div className="sm:col-span-2">
              <Button data-testid="car-round-submit" disabled={busy} onClick={publishRound}>
                <Drop size={16} className="mr-1.5" /> Publish the round
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ---- Register a business ---- */}
      <section className="border border-border bg-white p-4 sm:p-5" data-testid="car-business-panel">
        <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
          Run a wash, a valeting van or a garage?
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Put the business up with your trade licence and insurance number and your own prices.
          An admin approves it before staff can book.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Input data-testid="car-biz-name" placeholder="Business name" value={biz.name}
            onChange={(e) => setBiz({ ...biz, name: e.target.value })} />
          <select data-testid="car-biz-kind" aria-label="Kind" value={biz.kind}
            onChange={(e) => setBiz({ ...biz, kind: e.target.value })}
            className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
            {data.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Input data-testid="car-biz-where" placeholder="Town or address you work out of" value={biz.where}
            onChange={(e) => setBiz({ ...biz, where: e.target.value })} />
          <Input data-testid="car-biz-radius" type="number" min="0" placeholder="How far you travel (miles)"
            value={biz.radius_miles} onChange={(e) => setBiz({ ...biz, radius_miles: e.target.value })} />
          <Input data-testid="car-biz-licence-authority" placeholder="Licence issued by" value={biz.licence_authority}
            onChange={(e) => setBiz({ ...biz, licence_authority: e.target.value })} />
          <Input data-testid="car-biz-licence" placeholder="Trade licence / registration number"
            value={biz.licence_number} onChange={(e) => setBiz({ ...biz, licence_number: e.target.value })} />
          <Input data-testid="car-biz-insurance" placeholder="Insurance number" value={biz.insurance_number}
            onChange={(e) => setBiz({ ...biz, insurance_number: e.target.value })} />
          <Input data-testid="car-biz-phone" placeholder="Phone" value={biz.phone}
            onChange={(e) => setBiz({ ...biz, phone: e.target.value })} />
        </div>
        <div className="mt-3">
          <p className="overline text-slate-500">What you do, and the price for a medium car</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {data.services.map((s) => (
              <div key={s.key} className="flex items-center gap-2 border border-border px-3 py-2">
                <input type="checkbox" checked={biz.services.includes(s.key)}
                  onChange={() => toggle(s.key)} aria-label={s.label}
                  data-testid={`car-biz-service-${s.key}`} className="h-4 w-4" />
                <span className="min-w-0 flex-1 truncate text-sm text-[#0F172A]">{s.label}</span>
                {biz.services.includes(s.key) && (
                  <input type="number" min="0" value={prices[s.key] ?? ""}
                    onChange={(e) => setPrices({ ...prices, [s.key]: e.target.value })}
                    data-testid={`car-biz-price-${s.key}`} aria-label={`${s.label} price`}
                    className="w-20 border border-border px-2 py-1 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>
        <Button data-testid="car-biz-submit" className="mt-3" disabled={busy} onClick={register}>
          <Wrench size={16} className="mr-1.5" /> Send for approval
        </Button>
      </section>

      {/* ---- Admin approvals ---- */}
      {data.can_manage && data.pending.length > 0 && (
        <section data-testid="car-pending">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Waiting for approval
          </h2>
          <div className="mt-3 space-y-3">
            {data.pending.map((b, i) => (
              <div key={b.id} className="border border-border bg-white p-4" data-testid={`car-pending-${i}`}>
                <p className="font-semibold text-[#0F172A]">{b.name} · {b.kind}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {b.where} · licence {b.licence_number}
                  {b.licence_authority ? ` (${b.licence_authority})` : ""}
                  {b.insurance_number ? ` · insured ${b.insurance_number}` : ""}
                </p>
                <Button data-testid={`car-approve-${i}`} size="sm" className="mt-2" disabled={busy}
                  onClick={() => act(() => api.post(`/car/businesses/${b.id}/approve?verified=true`), "Approved")}>
                  Approve
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
